import * as THREE from 'three';
import { WeaponDef } from '../types';

// A compact low-poly viewmodel built from primitives (no external models),
// silhouette varies by weapon category so pistols, rifles, snipers and
// shotguns all read differently in the bottom-right of the screen.
export function buildViewmodel(weapon: WeaponDef): THREE.Group {
  const group = new THREE.Group();

  const metal = new THREE.MeshLambertMaterial({ color: 0x2b2b2b });
  const grip = new THREE.MeshLambertMaterial({ color: 0x1a1a1a });
  const accent = new THREE.MeshLambertMaterial({ color: weapon.color });

  // Grip (bottom, angled back toward the player)
  const gripMesh = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.22, 0.09), grip);
  gripMesh.position.set(0, -0.13, 0.08);
  gripMesh.rotation.x = 0.35;
  group.add(gripMesh);

  // Body / receiver
  const bodyLength = weapon.category === 'sniper' || weapon.category === 'rifle' ? 0.55 : 0.32;
  const bodyMesh = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.1, bodyLength), metal);
  bodyMesh.position.set(0, 0, -bodyLength / 2 + 0.12);
  group.add(bodyMesh);

  // Accent stripe on the body so each weapon still has a distinct color read
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.112, 0.02, bodyLength * 0.6), accent);
  stripe.position.set(0, 0.05, -bodyLength / 2 + 0.12);
  group.add(stripe);

  // Barrel
  const barrelLength = weapon.category === 'sniper' ? 0.5 : weapon.category === 'shotgun' ? 0.4 : 0.28;
  const barrelRadius = weapon.category === 'shotgun' ? 0.045 : 0.03;
  const barrelMesh = new THREE.Mesh(new THREE.CylinderGeometry(barrelRadius, barrelRadius, barrelLength, 8), metal);
  barrelMesh.rotation.x = Math.PI / 2;
  barrelMesh.position.set(0, 0.01, -bodyLength + 0.12 - barrelLength / 2);
  group.add(barrelMesh);

  if (weapon.category === 'shotgun') {
    // Second barrel for a pump/double read
    const barrel2 = barrelMesh.clone();
    barrel2.position.y -= 0.055;
    group.add(barrel2);
  }

  if (weapon.category === 'rifle' || weapon.category === 'sniper') {
    // Stock extending back toward the player
    const stock = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.09, 0.22), grip);
    stock.position.set(0, -0.01, 0.28);
    group.add(stock);
  }

  if (weapon.category === 'sniper') {
    // Simple scope on top
    const scopeBody = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.16, 8), metal);
    scopeBody.rotation.x = Math.PI / 2;
    scopeBody.position.set(0, 0.09, -0.05);
    group.add(scopeBody);
  }

  // Small front sight / magazine nub for silhouette read on pistols/SMGs
  if (weapon.category === 'pistol' || weapon.category === 'smg') {
    const mag = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.14, 0.05), grip);
    mag.position.set(0, -0.16, -0.02);
    group.add(mag);
  }

  group.scale.setScalar(1.0);
  return group;
}
